import React, { useEffect, useRef, useState } from 'react'

import { urlDoSocketComSessao } from '@nebula/shared/services/consoleApi'

interface VNCMetrics {
  resolution: string
  fps: number
  bandwidth: number
}

interface VNCConsoleProps {
  websocketUrl: string
  instanceId?: string
  vncTicket?: string
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Error) => void
  onMetricsUpdate?: (metrics: VNCMetrics) => void
  className?: string
}

interface VNCState {
  connected: boolean
  connecting: boolean
  error: string | null
}

const VNCConsole = React.forwardRef<any, VNCConsoleProps>(
  (
    { websocketUrl, vncTicket, onConnect, onDisconnect, onError, onMetricsUpdate, className = '' },
    ref,
  ) => {
    const canvasRef = useRef<HTMLDivElement>(null)
    const rfbRef = useRef<any>(null)
    // Cada connect/disconnect avança a geração: ticket ou socket que chega depois disso é descartado.
    const geracaoRef = useRef(0)
    const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const resizeObserverRef = useRef<ResizeObserver | null>(null)
    const metricsIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const metricsDataRef = useRef<{
      lastUpdate: number
      lastBytesReceived: number
      updateCount: number
    } | null>(null)
    const [state, setState] = useState<VNCState>({
      connected: false,
      connecting: false,
      error: null,
    })

    // Auto-resize VNC session to fit container
    const handleResize = () => {
      if (!rfbRef.current || !canvasRef.current) {
        return
      }

      // Check if we have an active canvas
      const canvas = canvasRef.current.querySelector('canvas')
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        return
      }

      // Clear existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }

      // Debounce resize to avoid too many resize calls
      resizeTimeoutRef.current = setTimeout(() => {
        try {
          const container = canvasRef.current
          if (!container) return

          // Enable NoVNC auto-scaling
          if (rfbRef.current) {
            rfbRef.current.scaleViewport = true
            rfbRef.current.clipViewport = false
            rfbRef.current.dragViewport = false
          }

          // Apply CSS scaling to make canvas fill the entire container
          const canvas = container.querySelector('canvas')
          if (canvas) {
            canvas.style.width = '100%'
            canvas.style.height = '100%'
            canvas.style.maxWidth = '100%'
            canvas.style.maxHeight = '100%'
            canvas.style.objectFit = 'fill' // Changed from 'contain' to 'fill' to use all space
            canvas.style.display = 'block'
          }
        } catch (error) {
          console.warn('⚠️ Error during VNC resize:', error)
        }
      }, 100) // Shorter debounce for better responsiveness
    }

    // Connect to VNC
    const connect = async () => {
      const target = canvasRef.current
      // Sem guarda de "já conectando" (#1079): a closure enxerga o `state` velho, e um connect
      // novo (URL trocada) tem de SUBSTITUIR o pendente — a geração descarta o antigo.
      if (!target) return

      setState((prev) => ({ ...prev, connecting: true, error: null }))
      const geracao = ++geracaoRef.current
      const obsoleta = () => geracao !== geracaoRef.current

      try {
        // Clear any existing connection
        if (rfbRef.current) {
          rfbRef.current.disconnect()
          rfbRef.current = null
        }

        // Clear the target element
        target.innerHTML = ''

        // Dynamic import of RFB from core (stable path)
        let RFB: any
        try {
          // @novnc/novnc 1.7+ exports field aponta direto pra core/rfb.js
          const module = await import('@novnc/novnc')
          RFB = module.default || module
        } catch (importError) {
          console.error('❌ Failed to load VNC module:', importError)
          setState((prev) => ({
            ...prev,
            connecting: false,
            error: 'Módulo VNC não disponível',
          }))
          onError?.(new Error('VNC module not available'))
          return
        }

        // Validate websocketUrl
        if (!websocketUrl || !websocketUrl.startsWith('ws')) {
          throw new Error(`Invalid WebSocket URL: ${websocketUrl}`)
        }

        // O JWT não vai na URL (#1079): o ticket de sessão (uso único, 30 s) é pedido por POST
        // autenticado por header. O ticket do PVE vem do corpo de /console/ticket (prop
        // `vncTicket`) e o agent entrega um novo no FRESH_TICKET — nenhum dos dois vai na URL.
        const finalWebsocketUrl = await urlDoSocketComSessao(websocketUrl)
        if (obsoleta()) return
        const resolvedTicket = vncTicket || ''

        if (!resolvedTicket) {
          throw new Error('Missing VNC ticket for authentication')
        }

        // Open WebSocket manually to intercept FRESH_TICKET message from agent.
        // In agent mode, the agent generates a fresh VNC ticket locally (the
        // pre-fetched one may not work for the RFB handshake). The backend sends
        // it as a text frame "FRESH_TICKET:<ticket>" before the VNC data starts.
        const wsUrl = finalWebsocketUrl.replace('https://', 'wss://').replace('http://', 'ws://')
        const ws = new WebSocket(wsUrl)
        ws.binaryType = 'arraybuffer'

        const freshTicket = await new Promise<string>((resolve, reject) => {
          const timeout = setTimeout(() => {
            // No FRESH_TICKET received within 3s — use original ticket
            ws.removeEventListener('message', onMessage)
            resolve(resolvedTicket)
          }, 3000)

          const onMessage = (event: MessageEvent) => {
            if (typeof event.data === 'string' && event.data.startsWith('FRESH_TICKET:')) {
              clearTimeout(timeout)
              ws.removeEventListener('message', onMessage)
              const ticket = event.data.substring('FRESH_TICKET:'.length)

              resolve(ticket)
            } else {
              // Non-ticket message arrived first — use original ticket
              clearTimeout(timeout)
              ws.removeEventListener('message', onMessage)
              resolve(resolvedTicket)
            }
          }

          ws.addEventListener('message', onMessage)
          ws.addEventListener('error', () => {
            clearTimeout(timeout)
            reject(new Error('WebSocket connection failed'))
          })
          ws.addEventListener('close', () => {
            clearTimeout(timeout)
            reject(new Error('WebSocket closed before VNC session started'))
          })
        })

        if (obsoleta()) {
          ws.close()
          return
        }

        // Pass the already-open WebSocket to RFB as a channel

        const rfb = new RFB(target, ws, {
          credentials: { password: freshTicket },
        })

        // Set up event listeners
        rfb.addEventListener('connect', () => {
          setState((prev) => ({ ...prev, connected: true, connecting: false, error: null }))

          // Trigger initial resize after connection is established
          setTimeout(() => {
            handleResize()
          }, 1000) // Give some time for the connection to stabilize

          // Start metrics collection
          if (onMetricsUpdate) {
            metricsIntervalRef.current = setInterval(updateMetrics, 1000) // Update every second
          }

          onConnect?.()
        })

        rfb.addEventListener('disconnect', (event: any) => {
          // Stop metrics collection
          if (metricsIntervalRef.current) {
            clearInterval(metricsIntervalRef.current)
            metricsIntervalRef.current = null
          }

          // Check if this is a connection failure (not a clean disconnect)
          if (!event.detail.clean && event.detail.code === 1006) {
            const errorMessage = websocketUrl.includes('addedlabs.com.br')
              ? 'Não foi possível estabelecer a conexão com o console remoto. Verifique se há bloqueios de rede ou firewall e entre em contato com o suporte.'
              : 'Conexão VNC perdida inesperadamente. Verifique se a VM foi migrada.'

            setState((prev) => ({
              ...prev,
              connected: false,
              connecting: false,
              error: errorMessage,
            }))
            onError?.(new Error(errorMessage))
          } else {
            setState((prev) => ({ ...prev, connected: false, connecting: false }))
          }

          onDisconnect?.()
        })

        rfb.addEventListener('credentialsrequired', () => {
          try {
            rfb.sendCredentials({ password: freshTicket })
          } catch (error) {
            console.error('❌ Failed to send VNC credentials:', error)
            setState((prev) => ({ ...prev, connecting: false, error: 'Falha na autenticação VNC' }))
          }
        })

        rfb.addEventListener('securityfailure', (event: any) => {
          console.error('🚨 VNC security failure:', event.detail)
          const error = new Error(`VNC security failure: ${event.detail.reason}`)
          setState((prev) => ({ ...prev, connecting: false, error: error.message }))
          onError?.(error)
        })

        // Configure RFB settings for optimal resize behavior
        rfb.scaleViewport = true
        rfb.resizeSession = false // Don't resize the VM session itself
        rfb.qualityLevel = 6
        rfb.compressionLevel = 2

        // Additional settings for better resize experience
        rfb.clipViewport = false // Don't clip the viewport
        rfb.dragViewport = false // Disable dragging since we want to fill the container

        // Configure cursor (clipboard disabled to avoid migration issues)
        rfb.showDotCursor = true

        rfbRef.current = rfb
      } catch (error: any) {
        if (obsoleta()) return
        console.error('❌ VNC connection error:', error)
        setState((prev) => ({ ...prev, connecting: false, error: error.message }))
        onError?.(error)
      }
    }

    // Disconnect from VNC
    const disconnect = () => {
      geracaoRef.current++
      if (rfbRef.current) {
        rfbRef.current.disconnect()
        rfbRef.current = null
      }
    }

    // Send key combination
    const sendKeys = (keys: string) => {
      if (!rfbRef.current || !state.connected) {
        return
      }

      try {
        // Map common key combinations
        switch (keys) {
          case 'ctrl+alt+del':
            rfbRef.current.sendKey(0xffe3, 'ControlLeft', true) // Ctrl down
            rfbRef.current.sendKey(0xffe9, 'AltLeft', true) // Alt down
            rfbRef.current.sendKey(0xffff, 'Delete', true) // Del down
            rfbRef.current.sendKey(0xffff, 'Delete', false) // Del up
            rfbRef.current.sendKey(0xffe9, 'AltLeft', false) // Alt up
            rfbRef.current.sendKey(0xffe3, 'ControlLeft', false) // Ctrl up
            break
          case 'ctrl+alt+t':
            rfbRef.current.sendKey(0xffe3, 'ControlLeft', true)
            rfbRef.current.sendKey(0xffe9, 'AltLeft', true)
            rfbRef.current.sendKey(0x0074, 'KeyT', true)
            rfbRef.current.sendKey(0x0074, 'KeyT', false)
            rfbRef.current.sendKey(0xffe9, 'AltLeft', false)
            rfbRef.current.sendKey(0xffe3, 'ControlLeft', false)
            break
          case 'alt+tab':
            rfbRef.current.sendKey(0xffe9, 'AltLeft', true)
            rfbRef.current.sendKey(0xff09, 'Tab', true)
            rfbRef.current.sendKey(0xff09, 'Tab', false)
            rfbRef.current.sendKey(0xffe9, 'AltLeft', false)
            break
          default:
            console.warn('Unknown key combination:', keys)
        }
      } catch (error) {
        console.error('Error sending keys:', error)
      }
    }

    // Collect and send VNC metrics
    const updateMetrics = () => {
      if (!rfbRef.current) return

      try {
        const rfb = rfbRef.current
        const canvas = canvasRef.current?.querySelector('canvas')

        // Get resolution from canvas
        let width = canvas?.width || 0
        let height = canvas?.height || 0

        if (!width || !height) {
          width = canvas?.clientWidth || 0
          height = canvas?.clientHeight || 0
        }

        const resolution = width && height ? `${width}x${height}` : 'N/A'
        const now = Date.now()

        // Initialize metrics data on first run
        if (!metricsDataRef.current) {
          metricsDataRef.current = {
            lastUpdate: now,
            lastBytesReceived: 0,
            updateCount: 0,
          }
        }

        const timeDelta = (now - metricsDataRef.current.lastUpdate) / 1000
        let bandwidth = 0
        let fps = 0

        try {
          const sock = (rfb as any)._sock

          if (sock && sock._rQlen !== undefined) {
            const currentBytes = sock._rQlen || 0

            if (timeDelta > 0 && metricsDataRef.current.lastBytesReceived > 0) {
              const bytesDelta = Math.abs(currentBytes - metricsDataRef.current.lastBytesReceived)
              bandwidth = bytesDelta / timeDelta / 1024 // KB/s
            }

            metricsDataRef.current.lastBytesReceived = currentBytes
          }

          // Estimate FPS based on bandwidth
          if (bandwidth > 0.1) {
            metricsDataRef.current.updateCount++
            if (bandwidth > 50) {
              fps = 30
            } else if (bandwidth > 10) {
              fps = 15
            } else {
              fps = Math.round(bandwidth * 0.5)
            }
          }
        } catch {
          // Silently fail
        }

        const metrics: VNCMetrics = {
          resolution,
          fps: Math.min(fps, 60),
          bandwidth: Math.max(0, bandwidth),
        }

        onMetricsUpdate?.(metrics)
        metricsDataRef.current.lastUpdate = now
      } catch {
        // Silently fail
      }
    }

    // Send text to VM (via keyboard simulation - works without clipboard=vnc)
    const sendText = (text: string) => {
      if (!rfbRef.current || !state.connected) {
        console.warn('⚠️ Console não conectado, não é possível enviar texto')
        return
      }

      try {
        // First try native clipboard if available
        if (rfbRef.current.clipboardPasteFrom) {
          rfbRef.current.clipboardPasteFrom(text)

          return
        }

        // Fallback: Simulate typing each character

        type CharacterDefinition = {
          keysym: number
          code?: string
          requiresShift?: boolean
        }

        const SHIFT_CHAR_MAP: Record<string, string> = {
          '!': 'Digit1',
          '@': 'Digit2',
          '#': 'Digit3',
          $: 'Digit4',
          '%': 'Digit5',
          '^': 'Digit6',
          '&': 'Digit7',
          '*': 'Digit8',
          '(': 'Digit9',
          ')': 'Digit0',
          _: 'Minus',
          '+': 'Equal',
          '{': 'BracketLeft',
          '}': 'BracketRight',
          '|': 'Backslash',
          ':': 'Semicolon',
          '"': 'Quote',
          '<': 'Comma',
          '>': 'Period',
          '?': 'Slash',
          '~': 'Backquote',
        }

        const BASE_KEY_CODE_MAP: Record<string, string> = {
          '`': 'Backquote',
          '0': 'Digit0',
          '1': 'Digit1',
          '2': 'Digit2',
          '3': 'Digit3',
          '4': 'Digit4',
          '5': 'Digit5',
          '6': 'Digit6',
          '7': 'Digit7',
          '8': 'Digit8',
          '9': 'Digit9',
          '-': 'Minus',
          '=': 'Equal',
          '[': 'BracketLeft',
          ']': 'BracketRight',
          '\\': 'Backslash',
          ';': 'Semicolon',
          "'": 'Quote',
          ',': 'Comma',
          '.': 'Period',
          '/': 'Slash',
        }

        const resolveCharacter = (char: string): CharacterDefinition | null => {
          if (!char) return null

          const codePoint = char.codePointAt(0)
          if (codePoint === undefined) return null

          if (char === '\n') {
            return { keysym: 0xff0d, code: 'Enter' }
          }

          if (char === '\r') {
            return null // handled by \n when present
          }

          if (char === '\t') {
            return { keysym: 0xff09, code: 'Tab' }
          }

          if (char === ' ') {
            return { keysym: 0x0020, code: 'Space' }
          }

          if (/[a-z]/.test(char)) {
            return { keysym: codePoint, code: `Key${char.toUpperCase()}` }
          }

          if (/[A-Z]/.test(char)) {
            return { keysym: codePoint, code: `Key${char}`, requiresShift: true }
          }

          if (SHIFT_CHAR_MAP[char]) {
            return {
              keysym: codePoint,
              code: SHIFT_CHAR_MAP[char],
              requiresShift: true,
            }
          }

          if (BASE_KEY_CODE_MAP[char]) {
            return {
              keysym: codePoint,
              code: BASE_KEY_CODE_MAP[char],
            }
          }

          if (codePoint >= 32 && codePoint <= 255) {
            return { keysym: codePoint }
          }

          return null
        }

        const pressKey = ({ keysym, code, requiresShift }: CharacterDefinition) => {
          if (requiresShift) {
            rfbRef.current.sendKey(0xffe1, 'ShiftLeft', true)
          }

          rfbRef.current.sendKey(keysym, code, true)
          rfbRef.current.sendKey(keysym, code, false)

          if (requiresShift) {
            rfbRef.current.sendKey(0xffe1, 'ShiftLeft', false)
          }
        }

        // Small delay between characters to ensure they're processed
        let charIndex = 0
        const typeCharacter = () => {
          if (charIndex < text.length) {
            const char = text[charIndex]
            const definition = resolveCharacter(char)

            if (definition) {
              pressKey(definition)
            } else {
              console.warn('⚠️ Caractere não suportado no clipboard manual:', char)
            }

            charIndex++
            setTimeout(typeCharacter, 10) // 10ms delay between characters
          } else {
          }
        }

        typeCharacter()
      } catch (error) {
        console.error('❌ Erro ao enviar texto:', error)
      }
    }

    // Get clipboard content from VM (limited without clipboard=vnc)
    const getClipboard = () => {
      if (!rfbRef.current || !state.connected) {
        console.warn('⚠️ Console não conectado, não é possível obter clipboard')
        return ''
      }

      try {
        // Try to get clipboard content if available (requires clipboard=vnc)
        if (rfbRef.current.clipboardText) {
          return rfbRef.current.clipboardText
        }

        console.warn('⚠️ Clipboard da VM não disponível sem clipboard=vnc. Use seleção manual.')
        return ''
      } catch (error) {
        console.error('❌ Erro ao obter clipboard:', error)
        return ''
      }
    }

    // Setup resize observers and cleanup on unmount
    useEffect(() => {
      // Add window resize listener
      window.addEventListener('resize', handleResize)

      // Setup ResizeObserver for container size changes
      if (canvasRef.current && typeof ResizeObserver !== 'undefined') {
        resizeObserverRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === canvasRef.current) {
              handleResize()
            }
          }
        })
        resizeObserverRef.current.observe(canvasRef.current)
      }

      return () => {
        // Cleanup
        window.removeEventListener('resize', handleResize)
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect()
        }
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current)
        }
        if (metricsIntervalRef.current) {
          clearInterval(metricsIntervalRef.current)
        }
        disconnect()
      }
    }, [])

    // Auto-connect when websocketUrl changes
    useEffect(() => {
      if (websocketUrl) {
        // Disconnect any existing connection before creating a new one
        disconnect()
        connect()
      }

      // Cleanup function to disconnect when component unmounts or websocketUrl changes
      return () => {
        disconnect()
      }
    }, [websocketUrl])

    // Expose methods via ref
    React.useImperativeHandle(ref, () => ({
      sendKeys,
      sendText,
      getClipboard,
      connect,
      disconnect,
      resize: handleResize,
      isConnected: () => state.connected,
    }))

    return (
      <div className={`vnc-console-container relative h-full w-full ${className}`}>
        {/* Connection status */}
        {state.connecting && (
          <div className="bg-opacity-75 absolute inset-0 z-10 flex items-center justify-center bg-black">
            <div className="text-center text-white">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-white"></div>
              <p className="text-lg">Conectando ao console...</p>
              <p className="mt-2 text-sm text-gray-400">Estabelecendo conexão VNC</p>
            </div>
          </div>
        )}

        {state.error && (
          <div className="bg-opacity-75 absolute inset-0 z-10 flex items-center justify-center bg-red-900">
            <div className="max-w-md rounded-lg bg-red-800 p-6 text-center text-white">
              <h3 className="mb-2 text-lg font-semibold">Erro de Conexão</h3>
              <p className="mb-4 text-sm">{state.error}</p>
              <button
                onClick={() => {
                  setState((prev) => ({ ...prev, error: null }))
                  connect()
                }}
                className="rounded bg-red-700 px-4 py-2 text-white transition-colors hover:bg-red-600"
              >
                Tentar Novamente
              </button>
            </div>
          </div>
        )}

        {/* VNC canvas container */}
        <div
          ref={canvasRef}
          className="relative flex h-full w-full items-center justify-center bg-black"
          style={{
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
          }}
        />
      </div>
    )
  },
)

VNCConsole.displayName = 'VNCConsole'

export default VNCConsole
